import { FC } from 'react';
import { shallow } from 'zustand/shallow';

import { Button } from '@/general/components/Button/Button';
import { formatNumber } from '@/general/helpers/formatNumber';
import { CarDisplayState } from '@/pdp/sections/Pdp/pdpHelpers';
import { pdpStore } from '@/stores/pdpStore';
import { BaseCarData } from '@/types/Car.types';
import { pushToDataLayer } from '@/utils/pushToDataLayer';
import { carDataToDataLayerItem } from '@/utils/pushToDataLayerProductCard';

import { PRICE_CARD_IDS } from '../CarPriceCard/CarPriceCard';
import { CarPriceCardDetails } from '../CarPriceCard/CarPriceCardDetails';

interface Props {
  displayState: CarDisplayState;
  isCheckoutProgress: boolean;
  carData: BaseCarData;
}

export const CarStickyBarContent: FC<Props> = ({ isCheckoutProgress, displayState, carData }) => {
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );
  const href = isCheckoutProgress ? `/checkout?sku=${carData.sku}` : `/product-detail/${carData.sku}/get-started`;

  const onOpenCarEnquiryForm = () => {
    setCurrentModal({ name: 'enquiry' });
    pushToDataLayer({
      event: 'lead_form_start',
      ecommerce: {
        items: [carDataToDataLayerItem(carData)],
      },
      entry_point: 'sticky_cta',
    });
  };

  return (
    <div className="flex justify-between gap-4 py-2">
      <div className="hidden flex-col justify-center sm:flex sm:justify-center md:w-full md:flex-row md:items-center md:justify-between md:gap-4">
        <header className="">
          <h2 className="c-fs-h6 font-extrabold">{carData.model}</h2>
          <div className="hidden md:block">
            <CarPriceCardDetails carData={carData} />
          </div>
        </header>
        <div className="gap-4 md:flex">
          <div
            className="c-fs-caption inline font-extrabold text-tex-def-tie2 md:block md:text-3xl md:text-tex-def-tie1"
            aria-describedby={PRICE_CARD_IDS.PRICE}
          >
            {formatNumber(carData.price, { prepend: '$', append: '*' })}
          </div>{' '}
          <div
            className="c-fs-caption inline text-tex-def-tie2 md:block md:w-36"
            aria-describedby={PRICE_CARD_IDS.FINANCE}
          >
            OR est. <b>{formatNumber(carData.weeklyRepayment ?? 0, { prepend: '$', append: '/wk' })}</b> 5 yr based on{' '}
            <b>10.02% p.a.</b>^
          </div>
        </div>
      </div>
      <div className="flex w-full items-center gap-2 sm:w-fit">
        {displayState === 'not-available' ? (
          <Button
            variant="primary"
            href={`/product-detail/${carData.sku}/similar`}
            data-testid="carStickyBarSimilarCars"
            fullWidth
          >
            View similar cars
          </Button>
        ) : (
          <>
            <Button
              href={displayState === 'loading' ? undefined : href}
              isLoading={displayState === 'loading'}
              isDisabledWhileLoading
              variant="accent"
              fullWidth
            >
              {displayState === 'continue-purchase' ? 'Continue purchase' : 'Get started'}
            </Button>
            <Button variant="secondary" onClick={onOpenCarEnquiryForm} data-testid="carStickyBarMessage" fullWidth>
              Message us
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
